/**
 * Shared duration formatting utilities.
 *
 * Replaces 20+ independent implementations across the frontend.
 *
 * @module lib/formatDuration
 */

/**
 * Format a duration in milliseconds to a human-readable string.
 *
 * Examples: "<1s", "42s", "3m 12s", "1h 5m"
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return '--';
  if (ms < 1000) return '<1s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Format elapsed wall time with the same fractional-second precision as CLI reports. */
export function formatElapsedDurationMs(milliseconds: number | null): string {
  if (milliseconds === null) return 'Not recorded';
  const seconds = milliseconds / 1000;
  const formatSeconds = (value: number) => `${Number(value.toFixed(3))}s`;
  if (seconds < 60) return formatSeconds(seconds);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  if (minutes < 60) {
    return remainder === 0 ? `${minutes}m` : `${minutes}m ${formatSeconds(remainder)}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;
  const remainSeconds = seconds - hours * 3600 - remainMinutes * 60;
  return [
    `${hours}h`,
    remainMinutes > 0 ? `${remainMinutes}m` : null,
    remainSeconds > 0 ? formatSeconds(remainSeconds) : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Format a duration from ISO start/end timestamps.
 *
 * If `finishedAt` is null/undefined, uses `Date.now()` (for in-progress runs).
 * Returns "\u2014" (em dash) if `startedAt` is missing.
 */
export function formatDurationFromIso(
  startedAt?: string | null,
  finishedAt?: string | null
): string {
  if (!startedAt) return '\u2014';
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  return formatDurationMs(Math.max(0, end - start));
}
